import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import PageHeader from '../components/PageHeader';
import { AbsenceSheet, ProposalSheet } from '../components/AppointmentSheets';
import { CheckIn, ProposalLine } from '../components/AppointmentCard';
import { addDays, parseISODate, today, weekDays } from '../lib/dates';
import { absenceLabel, clock, localDate, weekLabel, weekQuery, weekdayShort } from '../lib/schedule';
import { remindersSupported } from '../lib/reminders';
import { useApp, useAppPath } from '../lib/AppContext';

const LEAD_TEXT = { 15: '15 phút', 30: '30 phút', 60: '1 giờ', 120: '2 giờ' };

function DayRow({ date, items, onTime, onAbsence, onChanged }) {
  const isToday = date === today();
  return (
    <li className="flex gap-3">
      <div className={`w-8 shrink-0 pt-3 text-center ${items.length ? '' : 'text-muted'}`}>
        <p className="text-[10px] font-semibold">{weekdayShort(date)}</p>
        <p className="tabular font-bold">{parseISODate(date).getDate()}</p>
      </div>
      {items.length === 0 ? <p className="flex min-h-12 flex-1 items-center text-sm text-muted">Nghỉ</p> : (
        <div className="flex flex-1 flex-col gap-2">
          {items.map((a) => {
            const absent = a.state === 'absent';
            return (
              <div key={a.id} className={`flex items-center gap-3 rounded-[16px] bg-base-100 p-3 ${isToday && !absent ? 'ring-2 ring-[#17804f]' : ''} ${absent || a.state === 'past' ? 'opacity-70' : ''}`}>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 font-bold">
                    Buổi tập
                    {isToday && !absent && <span className="rounded-full bg-[#17804f] px-2 py-0.5 text-[10px] font-bold text-white">Hôm nay</span>}
                    {absent && <span className="rounded-full bg-[#fdebe4] px-2 py-0.5 text-[10px] font-bold text-[#8e3414]">Báo nghỉ</span>}
                  </p>
                  {a.note && <p className="text-sm font-semibold text-[#17804f]">{a.note}</p>}
                  <p className="text-xs text-muted">{[a.location, `${a.duration_min} phút`].filter(Boolean).join(' · ')}</p>
                  {absent && a.absence.reason && <p className="text-xs text-muted">{absenceLabel(a.absence.reason)}</p>}
                  {!absent && <ProposalLine proposal={a.proposal} />}
                  {!absent && <div className="mt-2"><CheckIn appointment={a} onChanged={onChanged} compact /></div>}
                  {a.can?.report_absence && (
                    <button type="button" className="min-h-8 text-xs font-semibold text-[#8e3414] underline" onClick={() => onAbsence(a)}>Báo nghỉ buổi này</button>
                  )}
                </div>
                <button type="button" disabled={!a.can?.propose} onClick={() => onTime(a)} aria-label={`${clock(a.starts_at)}, bấm để xin đổi giờ`}
                  className="flex h-10 shrink-0 items-center gap-1 rounded-[12px] border border-line px-3 font-bold tabular disabled:border-transparent">
                  {clock(a.starts_at)}{a.can?.propose && <Icon name="chevronDown" className="size-4 text-muted" />}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </li>
  );
}

// B2 "Lịch tập với Coach": the week's appointments, the time the coach set on each (tap it to ask for another
// time), what is waiting for the coach, and how long before each session the phone reminds.
export default function SchedulePage() {
  const { api, reminders } = useApp();
  const path = useAppPath();
  const [params, setParams] = useSearchParams();
  const [week, setWeek] = useState(params.get('date') || today());
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [proposing, setProposing] = useState(null);
  const [reporting, setReporting] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(() => api.get(`/appointments?${weekQuery(week)}`).then(setData).catch((e) => setError(e.message)), [api, week]);
  useEffect(() => { setData(null); load(); }, [load]);

  // A reminder's "Xin đổi giờ" / "Nghỉ hôm nay" opens the sheet for that appointment.
  useEffect(() => {
    const id = Number(params.get('appointment'));
    const sheet = params.get('sheet');
    if (!data || !id || !sheet) return;
    const a = data.appointments.find((x) => x.id === id);
    if (a && sheet === 'reschedule' && a.can.propose) setProposing(a);
    if (a && sheet === 'absence' && a.can.report_absence) setReporting(a);
    setParams({}, { replace: true });
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = (updated, message) => {
    setProposing(null);
    setReporting(null);
    setData((d) => ({ ...d, appointments: d.appointments.map((a) => (a.id === updated.id ? updated : a)) }));
    setToast(message);
    setTimeout(() => setToast(null), 3500);
  };

  const days = weekDays(week);
  const byDay = (d) => (data?.appointments || []).filter((a) => localDate(a.starts_at) === d);
  const count = (data?.appointments || []).filter((a) => a.state !== 'absent').length;
  const coachName = data?.coach?.name;

  return (
    <div className="flex flex-col gap-3">
      <PageHeader title="Lịch tập với Coach" fallback="/day" />
      {error && <div role="alert" className="rounded-[20px] bg-[#fdebe4] p-4 text-sm text-[#8e3414]">{error}</div>}
      {!data && !error && <div className="skeleton h-64 rounded-[20px]" />}

      {data && !data.coach && (
        <section className="flex flex-col items-start gap-3 rounded-[20px] bg-base-100 p-5">
          <p className="font-bold">Bạn chưa kết nối với huấn luyện viên</p>
          <p className="text-sm text-muted">Khi kết nối, lịch hẹn tập với Coach sẽ hiện ở đây.</p>
          <Link to={path('/my-coach')} className="btn h-11 rounded-2xl">Kết nối với huấn luyện viên</Link>
        </section>
      )}

      {data?.coach && (
        <>
          <div className="flex items-center gap-3 rounded-[18px] bg-base-100 p-3">
            <Avatar name={coachName} url={data.coach.avatar_url} size={32} />
            <p className="text-sm"><b>Coach {coachName}</b> hẹn {count} buổi {week === today() || days.includes(today()) ? 'tuần này' : 'tuần đó'}</p>
          </div>
          <Link to={path('/settings/notifications')} className="flex min-h-12 items-center justify-between gap-2 rounded-[16px] bg-[#e3f1e9] px-4 text-sm">
            <span className="flex items-center gap-2">
              <Icon name="bell" className="size-4" />
              {remindersSupported()
                ? (reminders?.appointments ? <>Nhắc trước <b>{LEAD_TEXT[reminders.lead_minutes]}</b> mỗi buổi tập</> : 'Đang tắt nhắc lịch tập')
                : 'Nhắc lịch tập có trên app điện thoại'}
            </span>
            <span className="rounded-full bg-base-100 px-3 py-1 text-xs font-bold text-[#17804f]">Đổi</span>
          </Link>
          <p className="px-1 text-xs text-muted">Giờ tập do Coach {coachName} đặt. Muốn đổi, bấm vào giờ để gửi đề xuất, Coach duyệt.</p>

          <div className="flex items-center justify-between rounded-[16px] bg-base-100 px-1 py-1">
            <button type="button" className="btn btn-circle btn-ghost size-11" aria-label="Tuần trước" onClick={() => setWeek(addDays(week, -7))}>
              <Icon name="chevronLeft" className="size-5" />
            </button>
            <span className="text-sm font-bold">{weekLabel(week)}</span>
            <button type="button" className="btn btn-circle btn-ghost size-11" aria-label="Tuần sau" onClick={() => setWeek(addDays(week, 7))}>
              <Icon name="chevronRight" className="size-5" />
            </button>
          </div>
          <ul className="flex flex-col gap-2">
            {days.map((d) => <DayRow key={d} date={d} items={byDay(d)} onTime={setProposing} onAbsence={setReporting}
              onChanged={(updated) => setData((x) => ({ ...x, appointments: x.appointments.map((a) => (a.id === updated.id ? updated : a)) }))} />)}
          </ul>
        </>
      )}

      <ProposalSheet appointment={proposing} onClose={() => setProposing(null)} onDone={done} />
      <AbsenceSheet appointment={reporting} onClose={() => setReporting(null)} onDone={done} />
      {toast && <div role="status" className="fixed inset-x-4 bottom-28 z-40 mx-auto max-w-sm rounded-2xl bg-[#17201b] px-4 py-3 text-center text-sm font-semibold text-white">{toast}</div>}
    </div>
  );
}
